import {
  compareOrderToQuote,
  defaultColorFor,
  formatMoney,
  formatPercent,
  linesToAsk,
  PriceBook,
  priceDifferenceAtToday,
  pricesMovedSinceQuote,
  QUOTE_MATCH_MAX_LINES,
  quoteCheckState,
  quoteDiscrepancyFingerprint,
  quoteDiscrepancyKey,
  quoteFixFor,
  quoteMarginImpact,
  quoteMatchCandidates,
  quoteSources,
  quoteReviewSignature,
  toDecimal,
  usablePairings,
  type MaterialOrder,
  type QuoteCheckResult,
  type QuoteMarginImpact,
  type QuoteCheckState,
  type QuoteDiscrepancy,
  type QuoteFix,
  type QuotePriceMoves,
  type RoofLayout,
  type Row,
} from "@roof-hawk/core";
import { useEffect, useMemo, useRef, useState } from "react";

import { t, tn } from "../../i18n";
import { useTrackedRows } from "../../lib/editHistory";
import { fmtDateTime } from "../../lib/format";
import { useAsync } from "../../lib/hooks";
import {
  fetchEstimateLadderInput,
  fetchQuoteCheckContext,
  fetchQuoteMatchSuggestions,
  requestQuoteMatch,
  updateRow,
} from "../../lib/queries";
import { useSession } from "../../session";
import { nextSort } from "./WorkOrderEditor";

/**
 * The material order, checked against the estimate it belongs to.
 *
 * It runs by itself, every time the order is open and every time it changes —
 * there is nothing a person has to supply, so there is nothing to press. What
 * it never does by itself is change the order: only one difference in three is
 * a mistake, and a check that "fixed" the other two would delete the nails and
 * undo a screw count somebody raised on purpose. So each difference offers the
 * quoted value and a Keep, and nothing blocks the order (ADR 0156).
 *
 * Typed lines it cannot place are sent to Jev, through the `quote-match` Edge
 * Function, and the pairings it is confident in are used. Every one it used is
 * named on screen with a way to say it is wrong (ADR 0153).
 */
export function useQuoteCheck(
  /** Null until the editor has loaded it; hooks cannot wait. */
  order: MaterialOrder | null,
  /** Null while the roof is still being read. */
  layouts: readonly RoofLayout[] | null,
  /** Why the roof could not be read, when it could not. */
  layoutsError: string | null = null,
  /**
   * `company_settings.quote_check_enabled` — whether any of this runs at all.
   *
   * Off from 2026-09-21 (ADR 0190), and off is the whole feature: no reads, no
   * comparison, nothing asked of Jev, no panel and no warning when the order is
   * placed. Unknown reads as off, so a panel never appears while the settings
   * load and is then taken away.
   */
  enabled = false,
) {
  /*
   * The order, or nothing at all while the check is switched off. Every read
   * below hangs off this one name, so off is genuinely off: no context, no
   * pairings, no ladder and therefore no result, rather than a panel that is
   * drawn and then hidden after the work has been done (ADR 0190).
   */
  const checking = enabled ? order : null;
  /*
   * Each read says which order it was for. `useAsync` keeps the last answer
   * for a render after its inputs change, so the read made before the order
   * had loaded would otherwise say "not on an estimate", and the suggestions
   * of nothing would make a reviewed order flash as unreviewed.
   */
  const context = useAsync(
    async () =>
      checking
        ? {
            orderId: checking.id,
            value: await fetchQuoteCheckContext(checking),
          }
        : null,
    [checking?.id, checking?.estimate_id, checking?.roof_template_id],
  );
  const suggestions = useAsync(
    async () =>
      checking
        ? {
            orderId: checking.id,
            rows: await fetchQuoteMatchSuggestions(checking.id),
          }
        : null,
    [checking?.id],
  );
  const contextData =
    order && context.data?.orderId === order.id ? context.data.value : null;
  const contextReady = Boolean(order) && context.data?.orderId === order?.id;
  const stored =
    order && suggestions.data?.orderId === order.id
      ? suggestions.data.rows
      : null;
  // The estimate's whole ladder, so a difference can be said as margin too.
  const ladder = useAsync(
    async () =>
      checking?.estimate_id
        ? await fetchEstimateLadderInput(checking.estimate_id, checking.job_id)
        : null,
    [checking?.estimate_id, checking?.job_id],
  );
  const [matchError, setMatchError] = useState<string | null>(null);
  const asked = useRef(new Set<string>());
  /** One request at a time, so an older answer never lands after a newer one. */
  const asking = useRef(false);

  const items = useMemo(() => order?.items ?? [], [order]);

  const result: QuoteCheckResult | null = useMemo(() => {
    // Nothing is compared until every input is this order's: a check against
    // a roof not read yet, or before Jev's answers are in, says something false.
    if (
      !enabled ||
      !order ||
      !contextData ||
      layouts === null ||
      stored === null
    ) {
      return null;
    }
    // What the estimate was generated from where it kept a copy, so a
    // template or catalogue edit since then is not read as a difference.
    // A deleted template leaves the estimate no link; the copy still names it.
    const sources = quoteSources(contextData.estimate.generation_snapshot, {
      templateId: contextData.estimate.roof_template_id,
      template: contextData.template,
      templateItems: contextData.templateItems,
      catalogue: contextData.catalogue,
      measurements: layouts,
      prices: contextData.prices,
      suppliers: contextData.suppliers,
    });
    return compareOrderToQuote({
      estimate: {
        ...contextData.estimate,
        roof_template_id: sources.templateId,
      },
      order: {
        roof_template_id: order.roof_template_id ?? null,
        supplier_id: order.supplier_id,
        status: order.status,
        items,
      },
      template: sources.template,
      templateItems: sources.templateItems,
      catalogue: sources.catalogue,
      measurements: sources.measurements,
      prices: new PriceBook([...sources.prices], [...sources.suppliers]),
      // A metal porch on a shingle house: its order is checked against the
      // porch's own package, frozen with the estimate (ADR 0166).
      areas: sources.areas,
      changeOrders: contextData.changeOrders,
      otherOrders: contextData.otherOrders,
      pairings: usablePairings(stored, items),
      estimateItems: contextData.estimateItems,
    });
  }, [enabled, contextData, layouts, order, items, stored]);

  // Ask about typed lines nobody has asked about yet. Once per wording per
  // open, so a save that failed does not become a request on every render.
  useEffect(() => {
    if (!enabled || !order || !result || stored === null || suggestions.loading)
      return;
    if (asking.current) return;
    const { itemIds, productIds } = quoteMatchCandidates(result);
    const fresh = linesToAsk(itemIds, items, stored).filter((id) => {
      const key = `${id}:${items.find((i) => i.id === id)?.name ?? ""}`;
      if (asked.current.has(key)) return false;
      asked.current.add(key);
      return true;
    });
    if (fresh.length === 0) return;
    asking.current = true;
    void (async () => {
      try {
        // In batches the function accepts, so a large order is still matched.
        for (let at = 0; at < fresh.length; at += QUOTE_MATCH_MAX_LINES) {
          await requestQuoteMatch({
            orderId: order.id,
            itemIds: fresh.slice(at, at + QUOTE_MATCH_MAX_LINES),
            productIds,
          });
        }
        setMatchError(null);
      } catch (e) {
        setMatchError(e instanceof Error ? e.message : String(e));
      } finally {
        asking.current = false;
        await suggestions.reload();
      }
    })();
  }, [enabled, result, suggestions, stored, items, order]);

  const margin: QuoteMarginImpact | null = useMemo(() => {
    if (!order || !ladder.data || result?.status !== "compared") return null;
    return quoteMarginImpact(
      ladder.data,
      order.id,
      items,
      result.expected,
      result.checkedWith,
    );
  }, [ladder.data, order, items, result]);

  // Today's prices against the quoted ones, so a rise can be requoted. Read
  // from today's rows, never the frozen copy the check itself used.
  const priceMoves: QuotePriceMoves | null = useMemo(() => {
    if (!order || !contextData || result?.status !== "compared") return null;
    return pricesMovedSinceQuote({
      expected: result.expected,
      catalogue: contextData.catalogue,
      prices: new PriceBook(
        [...contextData.prices],
        [...contextData.suppliers],
      ),
      generationSupplierId: contextData.estimate.generation_supplier_id ?? null,
      orderStatus: order.status,
    });
  }, [contextData, order, result]);

  const state: QuoteCheckState | null = result
    ? quoteCheckState(result, order?.quote_reviewed_signature)
    : null;

  const error =
    context.error ??
    suggestions.error ??
    (layoutsError
      ? t("the roof measurements could not be read: {error}", {
          error: layoutsError,
        })
      : null);

  return {
    /**
     * The check is switched off (ADR 0190). Every reader stops here: the panel
     * draws nothing and placing the order says nothing about the quote.
     */
    off: !enabled,
    loading:
      enabled && !error && (!contextReady || (contextData !== null && !result)),
    error,
    retrying: context.loading || suggestions.loading,
    retry:
      context.error || suggestions.error
        ? () => Promise.all([context.reload(), suggestions.reload()])
        : null,
    notOnEstimate: contextReady && contextData === null && !error,
    context: contextData,
    result,
    state,
    margin,
    priceMoves,
    suggestions: stored ?? [],
    reloadSuggestions: suggestions.reload,
    matchError,
  };
}

export type QuoteCheck = ReturnType<typeof useQuoteCheck>;

/** The line placing an order shows, or null when there is nothing to say. */
export function quotePlacementWarning(check: QuoteCheck): string | null {
  // Switched off: the order was never checked, so there is nothing to say
  // about the quote — including that it could not be checked (ADR 0190).
  if (check.off) return null;
  // An order that could not be checked is not an order that matches.
  const stillSend = t("It can still be sent.");
  if (check.error) {
    return `${t("This order could not be checked against the quote: {error}.", { error: check.error })} ${stillSend}`;
  }
  if (check.loading) {
    return `${t("This order has not finished being checked against the quote.")} ${stillSend}`;
  }
  const moved = priceMovesSentence(check.priceMoves);
  if (check.state !== "unreviewed" || check.result?.status !== "compared") {
    return moved ? `${moved} ${stillSend}` : null;
  }
  const n = check.result.discrepancies.length;
  const profit =
    check.margin && check.margin.grossProfitImpact !== 0
      ? ` ${t(
          "As ordered the job makes {ordered} ({orderedMargin}), against {quoted} ({quotedMargin}) as quoted.",
          {
            ordered: formatMoney(check.margin.grossProfitAsOrdered),
            orderedMargin: formatPercent(check.margin.marginAsOrdered),
            quoted: formatMoney(check.margin.grossProfitAsQuoted),
            quotedMargin: formatPercent(check.margin.marginAsQuoted),
          },
        )}`
      : "";
  const unreviewed = tn(
    n,
    "This order has 1 difference from the quote that nobody has reviewed.",
    "This order has {n} differences from the quote that nobody has reviewed.",
  );
  return `${unreviewed}${profit}${moved ? ` ${moved}` : ""} ${stillSend}`;
}

/** What moved prices do to the job, in one sentence, or null if none moved. */
export function priceMovesSentence(
  priceMoves: QuotePriceMoves | null,
): string | null {
  if (!priceMoves || priceMoves.moves.length === 0) return null;
  const n = priceMoves.moves.length;
  const parts = tn(
    n,
    "1 quoted part has changed price since the quote",
    "{n} quoted parts have changed price since the quote",
  );
  if (priceMoves.effect === 0) return `${parts}.`;
  const direction = priceMoves.effect > 0 ? t("more") : t("less");
  const amount = formatMoney(
    Math.abs(priceMoves.effect) as typeof priceMoves.effect,
  );
  return t(
    "{parts}: the materials now cost {amount} {direction} than quoted, so the estimate may need requoting.",
    { parts, amount, direction },
  );
}

const KIND_LABEL: Record<QuoteDiscrepancy["kind"], string> = {
  qty: "Quantity",
  price: "Price",
  not_ordered: "On the quote, not ordered",
  not_quoted: "Ordered, not on the quote",
  swapped: "Swapped",
};

const FIX_LABEL: Record<QuoteFix, string> = {
  set_to_quoted: "Set to quoted",
  put_quoted_back: "Put quoted back",
  remove: "Remove",
  add: "Add",
};

/** A quantity, with its squares beside it when the part is bought by area. */
function quantity(qty: number | null, squares: number | null): string {
  if (qty === null) return "—";
  return squares === null
    ? String(qty)
    : t("{qty} ({squares} SQ)", { qty, squares });
}

const CANNOT: Record<string, string> = {
  no_template:
    "Not checked against the quote: this estimate was built by hand or before 16 Sept 2026, so there is nothing to compare with.",
  no_answers:
    "Not checked against the quote: the estimate does not record the answers it was generated with.",
};

/** Why the order could not be compared, in the screen's language. */
function cannotSay(reason: string): string {
  const said = CANNOT[reason];
  return said ? t(said) : "";
}

export function QuoteCheckPanel({
  order,
  check,
  onReviewed,
}: {
  order: MaterialOrder;
  check: QuoteCheck;
  /** Stamps the order as reviewed. The editor's own patch, so it reloads. */
  onReviewed: (values: Record<string, unknown>) => Promise<void>;
}) {
  const { profileId } = useSession();
  const lines = useTrackedRows("material_order_items");
  const [kept, setKept] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);
  /** The fingerprint last written, so one review is recorded once. */
  const recorded = useRef<string | null>(null);
  const items = order.items ?? [];

  /*
   * Kept some, then set the rest to quoted: every difference left has been
   * dealt with, so the review is recorded the same as if each had been kept.
   */
  const { result: checked, state } = check;
  useEffect(() => {
    if (state !== "unreviewed" || checked?.status !== "compared") return;
    const left = checked.discrepancies;
    if (left.length === 0 || kept.size === 0) return;
    if (!left.every((d) => kept.has(quoteDiscrepancyFingerprint(d)))) return;
    const signature = quoteReviewSignature(left);
    if (recorded.current === signature) return;
    recorded.current = signature;
    setReviewError(null);
    onReviewed({
      quote_reviewed_signature: signature,
      quote_checked_at: new Date().toISOString(),
    }).catch((e: unknown) => {
      // Not written, so not reviewed. The keeps are dropped rather than
      // retried here, which would write on every render while the cause
      // lasts: pressing Keep again is the retry.
      recorded.current = null;
      setKept(new Set());
      setReviewError(e instanceof Error ? e.message : String(e));
    });
  }, [checked, state, kept, onReviewed]);

  // Switched off (ADR 0190). Nothing at all — not a line saying the check is
  // off, because an order nobody is checking should read like an order, and a
  // note about an absent feature is the clutter this was turned off to remove.
  if (check.off) return null;

  if (check.error) {
    return (
      <div className="config-warn" role="status">
        {t("Could not check this order against the quote: {error}", {
          error: check.error,
        })}{" "}
        {check.retry && (
          <button
            className="btn-small"
            disabled={check.retrying}
            onClick={() => void check.retry?.()}
          >
            {t("Retry quote check")}
          </button>
        )}
      </div>
    );
  }
  if (check.notOnEstimate) {
    return (
      <div className="na-hint" role="status">
        {t("Not checked against a quote: this order is not on an estimate.")}
      </div>
    );
  }
  if (check.loading || !check.result) {
    return (
      <div className="na-hint" role="status">
        {t("Checking this order against the quote…")}
      </div>
    );
  }

  const { result } = check;

  if (result.status === "cannot_compare") {
    const names = check.context?.templateNames ?? {};
    const sentence =
      result.reason === "different_template"
        ? t(
            'Not checked line by line: this order was generated from "{order}" and the estimate from "{estimate}".',
            {
              order:
                names[result.orderTemplateId ?? ""] ?? t("another template"),
              estimate:
                names[result.estimateTemplateId ?? ""] ?? t("a different one"),
            },
          )
        : cannotSay(result.reason);
    return (
      <div className="config-warn" role="status">
        {sentence}
      </div>
    );
  }

  const pairingNotes = result.proposedPairings.map((pairing) => {
    const item = items.find((i) => i.id === pairing.orderItemId);
    const part = check.context?.catalogue.find(
      (p) => p.id === pairing.productId,
    );
    const stored = check.suggestions.find(
      (s) => s.order_item_id === pairing.orderItemId,
    );
    return { pairing, item, part, stored };
  });

  async function rejectPairing(suggestionId: string) {
    setBusy(true);
    try {
      await updateRow("quote_match_suggestions", suggestionId, {
        accepted: false,
        decided_by: profileId ?? null,
        decided_at: new Date().toISOString(),
      });
      await check.reloadSuggestions();
    } finally {
      setBusy(false);
    }
  }

  const productName = (id: string) =>
    check.context?.catalogue.find((p) => p.id === id)?.name ??
    t("another product");
  const soldSentence = (sold: NonNullable<typeof result.sold>): string => {
    const vars = {
      sold: sold.product ? productName(sold.product.soldProductId) : "",
      template: sold.product ? productName(sold.product.templateProductId) : "",
      squares: sold.squares?.sold ?? "",
      measured: sold.squares?.measured ?? "",
    };
    if (sold.product && sold.squares)
      return t(
        "Checked against what the estimate sells: {sold}, not the template's {template}, and {squares} squares sold against {measured} measured.",
        vars,
      );
    if (sold.product)
      return t(
        "Checked against what the estimate sells: {sold}, not the template's {template}:.",
        vars,
      );
    if (sold.squares)
      return t(
        "Checked against what the estimate sells: {squares} squares sold against {measured} measured.",
        vars,
      );
    return t("Checked against what the estimate sells:.");
  };
  const soldNote = result.sold && (
    <div className="na-hint">{soldSentence(result.sold)}</div>
  );

  const moves = check.priceMoves?.moves ?? [];
  const priceNote = moves.length > 0 && (
    <div className="config-warn">
      {priceMovesSentence(check.priceMoves)}
      <ul>
        {moves.map((m) => (
          <li key={m.productId}>
            {t(
              "{part}: {quoted} quoted, {today} today, on {qty} ({sign}{effect})",
              {
                part: m.description,
                quoted: formatMoney(m.quoted),
                today: formatMoney(m.today),
                qty: m.qty,
                sign: m.effect > 0 ? "+" : "−",
                effect: formatMoney(Math.abs(m.effect) as typeof m.effect),
              },
            )}
          </li>
        ))}
      </ul>
    </div>
  );

  const jevNotes = (
    <>
      {priceNote}
      {soldNote}
      {pairingNotes.map(({ pairing, item, part, stored }) => (
        <div className="na-hint" key={pairing.orderItemId}>
          {t("Jev matched “{line}” to {part} ({sure} sure).", {
            line: item?.name ?? t("a typed line"),
            part: part?.name ?? t("a quoted part"),
            sure: formatPercent(pairing.probability),
          })}{" "}
          {stored && (
            <button
              className="btn-small"
              disabled={busy}
              onClick={() => void rejectPairing(stored.id)}
            >
              {t("Not the same part")}
            </button>
          )}
        </div>
      ))}
      {check.matchError && (
        <div className="na-hint">
          {t("Typed lines were not matched: {error}", {
            error: check.matchError,
          })}
        </div>
      )}
      {reviewError && (
        <div className="config-warn">
          {t(
            "The review was not saved, so this order still reads as unreviewed: {error}",
            { error: reviewError },
          )}
        </div>
      )}
    </>
  );

  if (check.state === "matches") {
    return (
      <div className="quote-check ok" role="status">
        <b>{t("Matches the quote.")}</b>
        {jevNotes}
      </div>
    );
  }

  const { discrepancies } = result;
  const reviewed = check.state === "reviewed";
  const isKept = (d: QuoteDiscrepancy) =>
    reviewed || kept.has(quoteDiscrepancyFingerprint(d));

  // Keeping only marks the row. The effect above records the review once
  // every difference left has been kept.
  const keep = (d: QuoteDiscrepancy) =>
    setKept((was) => new Set(was).add(quoteDiscrepancyFingerprint(d)));
  const keepAll = () =>
    setKept(new Set(discrepancies.map(quoteDiscrepancyFingerprint)));

  /** Put the order back to what the quote says, through undo. */
  async function setToQuoted(d: QuoteDiscrepancy) {
    setBusy(true);
    try {
      // The table helpers take any row; the same cast OrderItemsTable makes.
      const found = items.find((i) => i.id === d.itemId);
      const row = found ? (found as unknown as Row) : undefined;
      if (d.kind === "qty" && row && d.quotedQty !== null) {
        await lines.patch(
          row,
          { qty: d.quotedQty },
          t("Set {part} to the quoted quantity", { part: d.description }),
        );
      } else if (d.kind === "price" && row && d.quotedPrice !== null) {
        await lines.patch(
          row,
          { unit_price: toDecimal(d.quotedPrice) },
          t("Set {part} to the quoted price", { part: d.description }),
        );
      } else if (d.kind === "swapped" && row && d.quotedPrice !== null) {
        const expected =
          result.status === "compared"
            ? result.expected.find((l) => l.product_id === d.productId)
            : undefined;
        const part = check.context?.catalogue.find((p) => p.id === d.productId);
        await lines.patch(
          row,
          {
            product_id: d.productId,
            name: d.description,
            qty: d.quotedQty,
            unit: expected?.unit ?? null,
            unit_price: toDecimal(d.quotedPrice),
            // The ordered part's colour and working are not the quoted
            // part's: a painted colour left on a galvalume panel goes to
            // the supplier as written.
            color: part ? defaultColorFor(part) : null,
            calc_note: expected?.calc ?? null,
          },
          t("Put {part} back in place of {ordered}", {
            part: d.description,
            ordered: d.orderedDescription ?? t("the ordered part"),
          }),
        );
      } else if (d.kind === "not_quoted" && row) {
        await lines.remove(row);
      } else if (d.kind === "not_ordered" && d.quotedPrice !== null) {
        const expected =
          result.status === "compared"
            ? result.expected.find((l) => l.product_id === d.productId)
            : undefined;
        await lines.add(
          {
            material_order_id: order.id,
            product_id: d.productId,
            name: d.description,
            qty: d.quotedQty,
            unit: expected?.unit ?? null,
            unit_price: toDecimal(d.quotedPrice),
            calc_note: expected?.calc ?? null,
            sort_order: nextSort(items),
          },
          t("Added {part} from the quote", { part: d.description }),
        );
      }
    } finally {
      setBusy(false);
    }
  }

  /** What the correcting button does for this row, if anything (core). */
  const fixOf = (d: QuoteDiscrepancy) => quoteFixFor(d, check.priceMoves);

  const n = discrepancies.length;

  return (
    <div
      className={`quote-check ${reviewed ? "reviewed" : "open"}`}
      role="status"
    >
      <div className="quote-check-head">
        <b>
          {tn(
            n,
            "1 difference from the quote",
            "{n} differences from the quote",
          )}
        </b>{" "}
        <span className="na-hint">
          {t("Order {ordered} against quoted {quoted} ({net}).", {
            ordered: formatMoney(result.orderedTotal),
            quoted: formatMoney(result.quotedTotal),
            net: formatMoney(result.netImpact),
          })}
        </span>
        {check.margin && check.margin.grossProfitImpact !== 0 && (
          <span className="na-hint">
            {t(
              "Margin {ordered} as ordered, {quoted} as quoted ({profit} of gross profit).",
              {
                ordered: formatPercent(check.margin.marginAsOrdered),
                quoted: formatPercent(check.margin.marginAsQuoted),
                profit: formatMoney(check.margin.grossProfitImpact),
              },
            )}
          </span>
        )}
        {reviewed ? (
          <span className="na-hint">
            {" "}
            {t("Reviewed")}
            {order.quote_checked_at
              ? ` ${fmtDateTime(order.quote_checked_at)}`
              : ""}
            .
          </span>
        ) : (
          <button className="btn-small" disabled={busy} onClick={keepAll}>
            {t("Keep all as ordered")}
          </button>
        )}
      </div>

      <div className="ewrap" style={{ marginTop: 8 }}>
        <table className="etable">
          <thead>
            <tr>
              <th>{t("What")}</th>
              <th>{t("Item")}</th>
              <th className="num">{t("Quoted")}</th>
              <th className="num">{t("Ordered")}</th>
              <th className="num">{t("Impact")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {discrepancies.map((d) => (
              <tr key={quoteDiscrepancyKey(d)}>
                <td className="sub">{t(KIND_LABEL[d.kind])}</td>
                <td>
                  {d.kind === "swapped" ? (
                    <>
                      <b>{d.orderedDescription}</b> {t("ordered,")}{" "}
                      <b>{d.description}</b> {t("quoted")}
                    </>
                  ) : (
                    d.description
                  )}
                </td>
                <td className="num">
                  {d.kind === "price"
                    ? d.quotedPrice === null
                      ? "—"
                      : formatMoney(d.quotedPrice)
                    : quantity(d.quotedQty, d.quotedSquares)}
                </td>
                <td className="num">
                  {d.kind === "price"
                    ? d.orderedPrice === null
                      ? "—"
                      : formatMoney(d.orderedPrice)
                    : quantity(d.orderedQty, d.orderedSquares)}
                </td>
                <td
                  className={`num ${d.costImpact > 0 ? "overrun" : "credit"}`}
                >
                  {formatMoney(d.costImpact)}
                </td>
                <td className="rowacts">
                  {isKept(d) ? (
                    <span className="na-hint">{t("Kept")}</span>
                  ) : (
                    <>
                      {priceDifferenceAtToday(d, check.priceMoves) && (
                        <span className="na-hint">
                          {t("Today’s price: requote rather than set back.")}
                        </span>
                      )}
                      {fixOf(d) && (
                        <button
                          className="btn-small"
                          disabled={busy}
                          onClick={() => void setToQuoted(d)}
                        >
                          {t(FIX_LABEL[fixOf(d)!])}
                        </button>
                      )}
                      <button
                        className="btn-small"
                        disabled={busy}
                        onClick={() => keep(d)}
                      >
                        {t("Keep")}
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {jevNotes}
    </div>
  );
}
